import styles from './InvestmentCaseScoreRadar.module.css';
import {
  INVESTMENT_CASE_RATING_DIMENSIONS,
  clampRating,
  investmentCaseAverageScore,
} from '../utils/investmentCase';
import type { InvestmentCase } from '../types';

const SIZE = 340;
const CENTER = SIZE / 2;
const RADIUS = 112;
const LABEL_RADIUS = 142;
const GRID_LEVELS = [2, 4, 6] as const;
const INVEST_THRESHOLD = 4;

type Props = {
  investmentCase: InvestmentCase;
};

function axisAngle(index: number, count: number): number {
  return -Math.PI / 2 + (index * 2 * Math.PI) / count;
}

function polar(angle: number, radius: number): { x: number; y: number } {
  return {
    x: CENTER + radius * Math.cos(angle),
    y: CENTER + radius * Math.sin(angle),
  };
}

function valueRadius(value: number): number {
  const v = clampRating(value);
  return ((v - 1) / 5) * RADIUS;
}

function ringPath(level: number, count: number): string {
  const r = ((level - 1) / 5) * RADIUS;
  return Array.from({ length: count }, (_, i) => {
    const { x, y } = polar(axisAngle(i, count), r);
    return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(' ')
    .concat(' Z');
}

function scorePath(values: number[]): string {
  const count = values.length;
  return values
    .map((value, i) => {
      const { x, y } = polar(axisAngle(i, count), valueRadius(value));
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ')
    .concat(' Z');
}

function labelAnchor(angle: number): 'start' | 'middle' | 'end' {
  const x = Math.cos(angle);
  if (x > 0.25) return 'start';
  if (x < -0.25) return 'end';
  return 'middle';
}

function labelDy(angle: number): number {
  const y = Math.sin(angle);
  if (y < -0.7) return -4;
  if (y > 0.7) return 12;
  return 4;
}

export function InvestmentCaseScoreRadar({ investmentCase }: Props) {
  const dimensions = INVESTMENT_CASE_RATING_DIMENSIONS;
  const values = dimensions.map(({ key }) => clampRating(investmentCase[key]));
  const average = investmentCaseAverageScore(investmentCase);
  const meetsInvestThreshold = average >= INVEST_THRESHOLD;

  const summary = dimensions
    .map(({ label, key }, i) => `${label} ${values[i]} of 6`)
    .join(', ');

  return (
    <div className={styles.wrap}>
      <h3 className={styles.heading}>Score profile</h3>

      <figure className={styles.chart}>
        <svg
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className={styles.svg}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={`Investment case ratings: ${summary}. Average ${average.toFixed(2)}.`}
        >
          <defs>
            <radialGradient id="scoreRadarGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="var(--shamrock-green)" stopOpacity="0.08" />
              <stop offset="100%" stopColor="var(--shamrock-green)" stopOpacity="0" />
            </radialGradient>
          </defs>

          <circle cx={CENTER} cy={CENTER} r={RADIUS + 8} fill="url(#scoreRadarGlow)" aria-hidden />

          {GRID_LEVELS.map((level) => (
            <path
              key={level}
              d={ringPath(level, dimensions.length)}
              className={level === INVEST_THRESHOLD ? styles.gridRingInvest : styles.gridRing}
              aria-hidden
            />
          ))}

          {dimensions.map(({ key }, i) => {
            const angle = axisAngle(i, dimensions.length);
            const outer = polar(angle, RADIUS);
            return (
              <line
                key={key}
                x1={CENTER}
                y1={CENTER}
                x2={outer.x}
                y2={outer.y}
                className={styles.axisLine}
                aria-hidden
              />
            );
          })}

          {GRID_LEVELS.map((level) => {
            const angle = axisAngle(0, dimensions.length);
            const { x, y } = polar(angle, ((level - 1) / 5) * RADIUS);
            return (
              <text
                key={`grid-${level}`}
                x={x}
                y={y - 5}
                textAnchor="middle"
                className={styles.gridLabel}
                aria-hidden
              >
                {level}
              </text>
            );
          })}

          <path
            d={scorePath(values)}
            className={meetsInvestThreshold ? styles.scoreFill : styles.scoreFillBelowThreshold}
          />

          {values.map((value, i) => {
            const angle = axisAngle(i, dimensions.length);
            const point = polar(angle, valueRadius(value));
            return (
              <circle
                key={dimensions[i].key}
                cx={point.x}
                cy={point.y}
                r={4.5}
                className={`${styles.scoreDot} ${
                  value >= INVEST_THRESHOLD ? styles.scoreDotInvest : styles.scoreDotBelowThreshold
                }`}
              >
                <title>{`${dimensions[i].label}: ${value}/6`}</title>
              </circle>
            );
          })}

          {dimensions.map(({ key, label }, i) => {
            const angle = axisAngle(i, dimensions.length);
            const labelPoint = polar(angle, LABEL_RADIUS);
            const scorePoint = polar(angle, LABEL_RADIUS + 14);
            return (
              <g key={key}>
                <text
                  x={labelPoint.x}
                  y={labelPoint.y}
                  dy={labelDy(angle)}
                  textAnchor={labelAnchor(angle)}
                  className={styles.axisLabel}
                >
                  {label}
                </text>
                <text
                  x={scorePoint.x}
                  y={scorePoint.y}
                  dy={labelDy(angle) + 12}
                  textAnchor={labelAnchor(angle)}
                  className={styles.axisScore}
                >
                  {values[i]}/6
                </text>
              </g>
            );
          })}

          <circle cx={CENTER} cy={CENTER} r={26} className={styles.centerBadge} aria-hidden />
          <text x={CENTER} y={CENTER + 1} textAnchor="middle" className={styles.centerValue}>
            {average.toFixed(1)}
          </text>
          <text x={CENTER} y={CENTER + 14} textAnchor="middle" className={styles.centerHint}>
            avg
          </text>
        </svg>
      </figure>
    </div>
  );
}
